import { BehaviorSubject, combineLatest, filter, firstValueFrom, map, of, switchMap } from 'rxjs';
import { Logger, DataLibrary, getDataFromApi, HttpRequestConfig, staticData } from 'army-builder-shared';
import { Injectable } from '@angular/core';

import { AutomatronComponent, Environment, Equipment, Faction, Leader, NaturalBehavior, Perk, ReferenceCard, UnitTemplate } from './models';
import { GenericWastelandCard } from './models/generic-wasteland-card.model';
import { EventCard } from './models/event.model';
import { BoostCard } from './models/boost.model';
import { ENTITY_LIBRARY_ACTIONS } from 'army-builder-shared/src/lib/global/army-builder-entity-store';
import { FWW_SELECTORS } from '../state/fww.selectors';
import { initialFWWLibraryState } from '../state/state';
import { GAME_IDS } from '../../game-id.token';

@Injectable({ providedIn: 'root' })
export class FWWDataLibrary extends DataLibrary {
    gameId = GAME_IDS.FWW;
    selectors = FWW_SELECTORS;

    static unitTemplates: UnitTemplate[] = [];
    static behaviors: NaturalBehavior[] = [];
    static explores: GenericWastelandCard[] = [];
    static strangers: GenericWastelandCard[] = [];
    static dangers: GenericWastelandCard[] = [];
    static creatures: GenericWastelandCard[] = [];
    static quests: GenericWastelandCard[] = [];
    static boosts: BoostCard[] = [];
    static events: EventCard[] = [];
    static environments: Environment[] = [];
    static battlefields: GenericWastelandCard[] = [];
    static purposes: GenericWastelandCard[] = [];
    static factions: Faction[] = [];

    static referenceCards: { [key: string]: ReferenceCard } = {};
    static equipment: { [key: string]: Equipment } = {};
    static perks: { [key: string]: Perk } = {};
    static leaders: { [key: string]: Leader } = {};
    static automatronPieces: { [key: string]: AutomatronComponent } = {};

    options$ = of([]);

    factions$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.factions)),
        filter((x) => !!x)
    );
    unitTemplates$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.unitTemplates)),
        filter((x) => !!x)
    );
    explores$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.explores)),
        filter((x) => !!x)
    );
    strangers$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.strangers)),
        filter((x) => !!x)
    );
    dangers$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.dangers)),
        filter((x) => !!x)
    );
    creatures$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.creatures)),
        filter((x) => !!x)
    );
    quests$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.quests)),
        filter((x) => !!x)
    );
    boosts$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.boosts)),
        filter((x) => !!x)
    );
    events$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.events)),
        filter((x) => !!x)
    );
    environments$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.environments)),
        filter((x) => !!x)
    );
    purposes$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.purposes)),
        filter((x) => !!x)
    );
    battlefields$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.battlefields)),
        filter((x) => !!x)
    );
    referenceCards$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.referenceCards)),
        filter((x) => !!x)
    );
    equipment$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.equipment)),
        filter((x) => !!x)
    );
    automatrons$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.automatrons)),
        filter((x) => !!x)
    );
    perks$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.perks)),
        filter((x) => !!x)
    );

    leaders$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.leaders)),
        filter((x) => !!x)
    );

    behaviors$ = this.ready$.pipe(
        switchMap(() => this.store.select(this.selectors.behaviors)),
        filter((x) => !!x),
        map((items) => items)
    );

    initGame(opts?: HttpRequestConfig) {
        const gameId = this.gameId;

        staticData(this.factions$, FWWDataLibrary.factions);
        staticData(this.unitTemplates$, FWWDataLibrary.unitTemplates);
        staticData(this.explores$, FWWDataLibrary.explores);
        staticData(this.strangers$, FWWDataLibrary.strangers);
        staticData(this.dangers$, FWWDataLibrary.dangers);
        staticData(this.creatures$, FWWDataLibrary.creatures);
        staticData(this.quests$, FWWDataLibrary.quests);
        staticData(this.boosts$, FWWDataLibrary.boosts);
        staticData(this.events$, FWWDataLibrary.events);
        staticData(this.environments$, FWWDataLibrary.environments);
        staticData(this.purposes$, FWWDataLibrary.purposes);
        staticData(this.battlefields$, FWWDataLibrary.battlefields);
        staticData(this.referenceCards$, FWWDataLibrary.referenceCards);
        staticData(this.equipment$, FWWDataLibrary.equipment);
        staticData(this.automatrons$, FWWDataLibrary.automatronPieces);
        staticData(this.perks$, FWWDataLibrary.perks);
        staticData(this.leaders$, FWWDataLibrary.leaders);
        staticData(this.behaviors$, FWWDataLibrary.behaviors);

        this.store.dispatch(ENTITY_LIBRARY_ACTIONS.INIT_GAME_LIBRARY({ gameId, initialState: initialFWWLibraryState }));
        getDataFromApi<Faction>(gameId, 'factions', opts);
        getDataFromApi<NaturalBehavior>(gameId, 'behaviors', opts);
        getDataFromApi<GenericWastelandCard>(gameId, 'explores', opts);
        getDataFromApi<GenericWastelandCard>(gameId, 'strangers', opts);
        getDataFromApi<GenericWastelandCard>(gameId, 'dangers', opts);
        getDataFromApi<GenericWastelandCard>(gameId, 'creatures', opts);
        getDataFromApi<GenericWastelandCard>(gameId, 'quests', opts);
        getDataFromApi<BoostCard>(gameId, 'boosts', opts);
        getDataFromApi<EventCard>(gameId, 'events', opts);
        getDataFromApi<Environment>(gameId, 'environments', opts);
        getDataFromApi<GenericWastelandCard>(gameId, 'purposes', opts);
        getDataFromApi<GenericWastelandCard>(gameId, 'battlefields', opts);
        getDataFromApi<ReferenceCard>(gameId, 'reference-cards', opts);
        getDataFromApi<Equipment>(gameId, 'equipment', opts);
        getDataFromApi<UnitTemplate>(gameId, 'automatrons', opts);
        getDataFromApi<Perk>(gameId, 'perks', opts);
        getDataFromApi<Leader>(gameId, 'leaders', opts);

        firstValueFrom(this.equipment$).then((_w) => {
            getDataFromApi<UnitTemplate>(gameId, 'unit-templates', opts);
        });
    }
}
